import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarGroup, Box, Tooltip, Typography } from '@mui/material';
import type { SharedUsersCellProps } from '../types';

/**
 * SharedUsersCell - Display shared users as avatar group
 *
 * Pure presentational component that shows shared users as avatars.
 * Displays up to 3 avatars with a "+N more" indicator for additional users.
 * Shows user details in tooltips.
 *
 * @param doc - The document to display shared users for
 */
export const SharedUsersCell: React.FC<SharedUsersCellProps> = ({ doc }) => {
  const { t } = useTranslation();

  // Try to use populated user details first, fallback to user IDs
  const userDetails = (doc as any).shared_with_users_details || [];
  const users = (doc as any).shared_with_users || [];

  if ((!userDetails || userDetails.length === 0) && (!users || users.length === 0)) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('documents.table.noUsers')}
      </Typography>
    );
  }

  // Helper function to get display name for a user
  const getUserDisplayName = (userDetail: any, userId?: string) => {
    if (userDetail && typeof userDetail === 'object') {
      if (userDetail.full_name) {return userDetail.full_name;}
      if (userDetail.email?.includes('@')) {
        const username = userDetail.email.split('@')[0];
        return username.replace('.', ' ').replace(/([A-Z])/g, ' $1').trim();
      }
      if (userDetail.username) {return userDetail.username;}
    }

    // Fallback to user ID
    if (userId && userId.length > 8) {
      return `${userId.slice(0, 8)}...`;
    }
    return userId || 'Unknown';
  };

  // Helper function to get initials from user details
  const getUserInitials = (userDetail: any, userId?: string) => {
    if (userDetail && typeof userDetail === 'object') {
      if (userDetail.full_name) {
        const parts = userDetail.full_name.trim().split(' ');
        if (parts.length >= 2) {
          return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        }
        return userDetail.full_name.slice(0, 2).toUpperCase();
      }
      if (userDetail.email?.includes('@')) {
        const name = userDetail.email.split('@')[0];
        return name.split('.').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
      }
      if (userDetail.username) {
        return userDetail.username.slice(0, 2).toUpperCase();
      }
    }

    // Fallback to user ID initials
    if (userId) {
      return userId.slice(0, 2).toUpperCase();
    }
    return '??';
  };

  // Use user details if available, otherwise fall back to user IDs
  const displayUsers = userDetails.length > 0
    ? userDetails.map((detail: any, index: number) => ({
        detail,
        userId: users[index] || null,
        displayName: getUserDisplayName(detail, users[index]),
        initials: getUserInitials(detail, users[index])
      }))
    : users.map((userId: string) => ({
        detail: null,
        userId,
        displayName: getUserDisplayName(null, userId),
        initials: getUserInitials(null, userId)
      }));

  const visibleUsers = displayUsers.slice(0, 3);
  const remainingCount = displayUsers.length - visibleUsers.length;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 20, height: 20, fontSize: '0.6rem' } }}>
        {visibleUsers.map((user: any, index: number) => {
          let tooltipText = user.displayName;
          if (user.detail?.email && user.detail.email !== user.displayName) {
            tooltipText += ` (${user.detail.email})`;
          } else if (user.userId && user.userId !== user.displayName) {
            tooltipText += ` (${user.userId})`;
          }

          return (
            <Tooltip key={index} title={tooltipText}>
              <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                {user.initials}
              </Avatar>
            </Tooltip>
          );
        })}
      </AvatarGroup>
      {remainingCount > 0 && (
        <Tooltip title={`${remainingCount} ${t('documents.table.moreUsers')}: ${displayUsers.slice(3).map((user: any) => user.displayName).join(', ')}`}>
          <Typography variant="caption" color="text.secondary">
            +{remainingCount}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

SharedUsersCell.displayName = 'SharedUsersCell';
